import { useTranslation } from "react-i18next";

import {
  ProductCardChip,
  simStatusToProductCardChipTone,
} from "@/components/Layout/BaseLayouts/ProductCard/ProductCardChip";
import {
  NumberPorting,
  NumberPortingStatus,
  SimCardDetailSimStatus,
  SimCardDetailSimStatusReason,
} from "@/services/model";
import { isLockRequested } from "@/utils/deviceUtils";

const statusLabelKey: Record<SimCardDetailSimStatus, string> = {
  green: "common.status.active",
  yellow: "layout.tiles.statusChip.pausing",
  red: "common.status.suspended",
  grey: "common.status.inactive",
  orange: "cockpit.filter.status-labels.onpPending",
};

export const SimStatusChip = ({
  status,
  numberPorting,
  servicePending,
  simStatusReason,
}: {
  status?: SimCardDetailSimStatus;
  /** When set, the chip matches the number-porting tile instead of simStatus. */
  numberPorting?: NumberPorting;
  /**
   * When true (and not overridden by number porting or orange missing-info),
   * matches PendingSimTile: "Being processed", or "Suspended" if renewal failed
   * (`LOCK_REQUESTED`).
   */
  servicePending?: boolean;
  simStatusReason?: SimCardDetailSimStatusReason;
}) => {
  const { t } = useTranslation("portal");

  // Orange ("missing info") already maps to the pending-porting label below.
  // A SIM that is still grey/yellow while porting would otherwise read as
  // inactive/active — the tile uses the porting copy, so the header must too.
  if (status !== SimCardDetailSimStatus.orange) {
    if (numberPorting?.status === NumberPortingStatus.dateKnown) {
      return (
        <ProductCardChip
          label={t("layout.tiles.numberPorting.numberBeingTransferred")}
          tone="info"
        />
      );
    }

    if (numberPorting) {
      return (
        <ProductCardChip
          label={t("cockpit.filter.status-labels.onpPending")}
          tone="warning"
        />
      );
    }
  }

  // Same gate as PendingSimTile in mapDevice (servicePending → pending tile).
  // Orange is NumberPortingTile (missing info) before pending, so keep that chip.
  if (servicePending && status !== SimCardDetailSimStatus.orange) {
    const couldNotRenew = isLockRequested({ simStatusReason });

    return (
      <ProductCardChip
        tone={couldNotRenew ? "error" : "info"}
        label={t(
          couldNotRenew
            ? "common.status.suspended"
            : "layout.tiles.pending.chipLabel",
        )}
      />
    );
  }

  if (!status) {
    return null;
  }

  return (
    <ProductCardChip
      label={t(statusLabelKey[status] as any)}
      tone={simStatusToProductCardChipTone[status]}
    />
  );
};
